import { useCallback } from 'react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { useFetchReducer } from './reducers/useFetchReducer';
import { delayAsync } from '../lib/delayAsync';
import { UseFetch } from './useFetch';
import { AppContext, useAppContext } from '../contexts/AppContext/AppContext';
import { PUBLIC_SESSION_KEY_HEADER } from '../application/browser/browser.utils';
import { windowLogger } from '../_lib/Logger/windowLogger';

export type AbortFn = () => void;

export function useGet<TResponse = any>(fallbackResponseWhenResponseINullOrUndefined?: TResponse) {
  const { startRequest, receivedResponse, receivedError, ...state } = useFetchReducer<TResponse, string>();
  const appContext: AppContext = useAppContext();

  const get: (url: string, useFetch?: UseFetch, config?: AxiosRequestConfig) => AbortFn = useCallback(
    (url: string, { delay = 0 }: UseFetch = {}, config: AxiosRequestConfig = {}) => {
      const controller = new AbortController();
      (async () => {
        const responsePromise = axios
          .get<TResponse>(url, {
            signal: controller.signal,
            headers: {
              // allows detecting if the backend session is not the same anymore:
              [PUBLIC_SESSION_KEY_HEADER]: appContext.publicSessionKey,
            },
            ...config,
          })
          .then((response) => {
            // handle successful response
            if (response.data) {
              return response;
            } else {
              return { ...response, data: fallbackResponseWhenResponseINullOrUndefined };
            }
          })
          .catch((e) => {
            // A few components throw AXIOS "ERR_CANCELED" errors, probably due to multiple fetch attempts.
            if (axios.isCancel(e)) {
              windowLogger.warn('Request canceled:', e);
            } else {
              receivedError(
                ((e as AxiosError).response?.data as any)?.errorType || 'errorLoadingContentPleaseTryAgain',
              );
            }
          });
        startRequest();
        await delayAsync(delay);
        try {
          const axiosResponse = await responsePromise;
          if (axiosResponse?.data) {
            receivedResponse(axiosResponse.data);
          }
        } catch (e) {
          receivedError(((e as AxiosError).response?.data as any)?.errorType || 'errorLoadingContentPleaseTryAgain');
        }
      })();
      return () => controller.abort();
    },
    [startRequest, receivedError, receivedResponse],
  );
  return { ...state, get };
}
